// Playground endpoint functions.
//
// These used to go through a Next BFF at `/pg/*` that injected a hand-crafted,
// *unsigned* trusted-system JWT and forwarded to the frozen `/api/v1` runtime.
// That token was only ever accepted by the `dev-local` dev-bypass row, so the
// Playground did not work in production (defect #5's guard). ADR-018 removed
// the BFF rather than replacing its token: every call below now lands on the
// tenant-admin surface, authorised by the same httpOnly session cookie as the
// rest of the console, with the runtime identity constructed server-side.
//
// The upshot for anything added here: **no runtime credential exists in the
// browser.** There is no token to fetch, refresh, or scope-check. Tenant
// scoping comes from the session, so no call takes a tenant identifier.

import type { AgentBundle, RunEnvelope } from "@/types/api";
import { apiFetch } from "./client";

export const getBundle = (slug: string, label: string) =>
  apiFetch<AgentBundle>(`/admin/my/agents/${slug}/versions/${label}/bundle`);

/** One run's current phase, plus the terminal envelope once it has one.
 *
 * How a deferred run reports its result: the Ably channel carries lifecycle
 * events, not the answer, so the client reads the answer from the run itself
 * when the channel says it is done. */
export const getRun = (runId: string) =>
  apiFetch<{
    run_id: string;
    phase: string;
    is_terminal: boolean;
    is_skill: boolean;
    envelope: RunEnvelope | null;
    final_state?: {
      skill_output?: Record<string, unknown> | null;
      /** The chat answer, once the audit row exists. */
      final_response?: string | null;
    } | null;
  }>(`/admin/my/runs/${runId}`);

// `runChat` / `runSkill` are deliberately absent. They were synchronous calls
// to the frozen runtime, and ADR-018 retired them rather than porting them:
// `startGovernedStream` in `./governed-stream` already covers both, over the
// checkpointed path that does not race an invocation deadline (ADR-013). One
// execution path for the Playground.
